import { BridgeChartInstance } from "../core/chart";
import type { BridgeChartInput } from "../types/data";
import type { BridgeChartOptions } from "../types/public";
import { terminalTheme } from "../themes/defaultTheme";
import { bindWorkspacePeriods } from "./workspacePeriod";
import { bindWorkspaceEvidence } from "./workspaceEvidence";
import { bindWorkspaceContext } from "./workspaceContext";

const islands = new Map<HTMLElement, { chart: BridgeChartInstance; json: string; dispose: () => void }>();

export function createChart(container: HTMLElement, options: BridgeChartOptions = {}): BridgeChartInstance {
  return new BridgeChartInstance(container, options);
}

export function initBridgeChartIslands(root: ParentNode = document): void {
  islands.forEach((entry, container) => {
    if (container.isConnected === false) {
      entry.dispose();
      entry.chart.destroy();
      islands.delete(container);
    }
  });
  root.querySelectorAll("[data-bridge-chart]").forEach(node => {
    const container = node as HTMLElement & { bridgeChart?: BridgeChartInstance };
    const dataNode = container.querySelector("[data-bridge-chart-data]");
    if (!dataNode) return;
    const json = dataNode.textContent || "{}";
    const previous = islands.get(container);
    if (previous?.json === json && container.dataset.bridgeChartReady === "true") return;
    previous?.dispose();
    previous?.chart.destroy();
    islands.delete(container);
    container.dataset.bridgeChartReady = "true";
    try {
      const data = JSON.parse(json) as BridgeChartInput;
      const workspace = container.dataset.chartContext === "workspace";
      container.bridgeChart = createChart(container, {
        data,
        ...(workspace ? { theme: terminalTheme, studies: [], showMarkers: false } : {}),
        presentation: { locale: container.lang?.startsWith("fa") ? "fa" : "en" }
      });
      const disposePeriods = workspace ? bindWorkspacePeriods(container, container.bridgeChart) : () => {};
      const disposeEvidence = workspace ? bindWorkspaceEvidence(container, container.bridgeChart, data) : () => {};
      const disposeContext = workspace ? bindWorkspaceContext(container, container.bridgeChart, data) : () => {};
      const dispose = () => { disposePeriods(); disposeEvidence(); disposeContext(); };
      islands.set(container, { chart: container.bridgeChart, json, dispose });
    } catch {
      container.dataset.bridgeChartError = "true";
      container.dataset.bridgeChartState = "error";
      showInitializationError(container);
    }
  });
}

export function render(root: HTMLElement, _canvas: HTMLCanvasElement, data: BridgeChartInput): BridgeChartInstance {
  return createChart(root, { data });
}

function showInitializationError(container: HTMLElement): void {
  const existing = container.querySelector("[data-bridge-chart-status]");
  const status = existing as HTMLElement | null ?? container.ownerDocument.createElement("div");
  status.className = "bridge-chart-status";
  status.setAttribute("data-bridge-chart-status", "");
  status.setAttribute("data-status", "error");
  status.setAttribute("role", "alert");
  status.setAttribute("aria-live", "assertive");
  const isPersian = container.lang.toLowerCase().startsWith("fa") || container.dir === "rtl";
  status.lang = isPersian ? "fa" : "en";
  status.dir = isPersian ? "rtl" : "ltr";
  status.textContent = isPersian
    ? "نمودار به‌دلیل نامعتبر بودن داده‌ها قابل نمایش نیست."
    : "The chart could not be rendered because its data is invalid.";
  status.hidden = false;
  if (!existing) (container.querySelector(".bridge-chart-stage") ?? container).appendChild(status);
}
